import BaseMixin from './base.mixin'
import { escapeHtml } from '../utils/text-format'
import { formatDateBR } from '../utils/date-validator'
import { printHtml } from '../utils/print-html-document'
import { showDialog } from '@/shared/sweetAlerts/showDialog'

const mixin = {
  mixins: [BaseMixin],
  methods: {
    async generateExamProtocol(examId) {
      if (!examId) {
        throw new Error('examId não informado!')
      }
      const res = await this.getClient().get('/core/v2/Exam/GenerateProtocol', {
        params: { examId }
      })
      return res.data?.data || res.data
    },
    formatarDataProtocolo(valor) {
      if (!valor) return ''
      return formatDateBR(valor) || String(valor)
    },
    montarImagemProtocolo(valor) {
      if (!valor) return ''
      const conteudo = String(valor)
      return conteudo.startsWith('data:') ? conteudo : `data:image/png;base64,${conteudo}`
    },
    montarHtmlProtocolo(dados) {
      const logo = this.montarImagemProtocolo(dados.logoB64)
      const qrCode = this.montarImagemProtocolo(dados.QRCodeB64 || dados.qrCodeB64)
      const link = dados.link || ''
      const titulo = escapeHtml(this.$t('print_protocol.title'))

      const celulas = [
        { label: this.$t('print_protocol.social_name'), value: dados.name },
        { label: this.$t('print_protocol.attendance_date'), value: this.formatarDataProtocolo(dados.examDate) },
        { label: this.$t('print_protocol.date_of_birth'), value: this.formatarDataProtocolo(dados.birthDate) },
        { label: this.$t('print_protocol.exam_type'), value: dados.specialty }
      ]

      const celulasHtml = celulas
        .map(
          (celula) =>
            `<div class="protocolo-celula"><span class="protocolo-label">${escapeHtml(celula.label)}</span>` +
            `<span class="protocolo-valor">${escapeHtml(celula.value)}</span></div>`
        )
        .join('')

      const logoHtml = logo ? `<img class="protocolo-logo" src="${escapeHtml(logo)}" alt="" />` : ''
      const qrCodeHtml = qrCode ? `<img class="protocolo-qrcode" src="${escapeHtml(qrCode)}" alt="" />` : ''
      const linkHtml = link ? `<div class="protocolo-link">${escapeHtml(link)}</div>` : ''

      return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${titulo}</title>
    <style>
      @page { margin: 0; }
      * { box-sizing: border-box; }
      body {
        font-family: Arial, Helvetica, sans-serif;
        color: #222;
        margin: 0;
      }
      .protocolo-card {
        max-width: 760px;
        margin: 24px auto;
      }
      .protocolo-moldura {
        border: 1px solid #e2e2e2;
        border-radius: 6px;
        padding: 24px;
      }
      .protocolo-header {
        display: grid;
        grid-template-columns: 1fr 2fr;
        align-items: center;
        border-bottom: 1px solid #e2e2e2;
        padding-bottom: 16px;
        margin-bottom: 24px;
      }
      .protocolo-logo {
        justify-self: start;
        height: 48px;
        width: auto;
        max-width: 200px;
        object-fit: contain;
      }
      .protocolo-titulo {
        text-align: center;
        text-decoration: underline;
        font-size: 22px;
        margin: 0;
      }
      .protocolo-body {
        display: flex;
        justify-content: space-between;
        gap: 24px;
      }
      .protocolo-dados {
        display: grid;
        grid-template-columns: 1.2fr 1fr;
        gap: 16px;
        flex: 1;
      }
      .protocolo-celula {
        display: flex;
        flex-direction: column;
      }
      .protocolo-label { font-weight: bold; font-size: 13px; }
      .protocolo-valor { font-size: 14px; }
      .protocolo-instrucoes {
        grid-column: 2;
        grid-row: 3;
        line-height: 1.4;
      }
      .protocolo-instrucoes .protocolo-valor { margin-top: 6px; }
      .protocolo-senha {
        align-self: flex-start;
        margin-top: 6px;
        background: #e0f2fe;
        color: #008ac4;
        border-radius: 4px;
        padding: 4px 6px;
        font-size: 12px;
        font-weight: bold;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .protocolo-qr {
        text-align: center;
      }
      .protocolo-qrcode {
        width: 150px;
        height: 150px;
        object-fit: contain;
      }
      .protocolo-link {
        font-size: 10px;
        word-break: break-word;
        max-width: 150px;
      }
    </style>
  </head>
  <body>
    <div class="protocolo-card">
      <div class="protocolo-moldura">
        <div class="protocolo-header">
          ${logoHtml}
          <h1 class="protocolo-titulo">${titulo}</h1>
        </div>
        <div class="protocolo-body">
          <div class="protocolo-dados">
            ${celulasHtml}
            <div class="protocolo-celula">
              <span class="protocolo-label">${escapeHtml(this.$t('print_protocol.attendance'))}</span>
              <span class="protocolo-valor">${escapeHtml(dados.examId)}</span>
              <span class="protocolo-senha">${escapeHtml(this.$t('print_protocol.web_password'))}: ${escapeHtml(dados.password)}</span>
            </div>
            <div class="protocolo-celula protocolo-instrucoes">
              <span class="protocolo-label">${escapeHtml(this.$t('print_protocol.access_title'))}</span>
              <span class="protocolo-valor">${escapeHtml(this.$t('print_protocol.access_instructions'))}</span>
            </div>
          </div>
          <div class="protocolo-qr">
            ${qrCodeHtml}
            ${linkHtml}
          </div>
        </div>
      </div>
    </div>
  </body>
</html>`
    },
    async imprimirProtocolo(examId) {
      if (!examId) return

      this.$root.$emit('showLoading')
      try {
        const dados = await this.generateExamProtocol(examId)
        await printHtml(this.montarHtmlProtocolo(dados))
      } catch (error) {
        showDialog({
          icon: 'error',
          title: this.$t('error.default'),
          text: error && error.message ? error.message : this.$t('print_protocol.error'),
          confirmButtonText: 'Ok'
        })
      } finally {
        this.$root.$emit('hideLoading')
      }
    }
  }
}

export default mixin
